// public/js/base/baseObject.js

import { createLogger } from '@utils/logger.js';

/**
 * BaseObject - PURE data container with schema definition
 * ONLY handles: data storage, schema definition
 * NO methods, NO business logic, NO display logic, NO validation logic
 */
class BaseObject {
    /**
     * @param {Object} data - Initial data for the object
     * @param {Object} schema - Schema definition with field configurations
     * @param {Object} options - Additional options
     */
    constructor(data = {}, schema = {}, options = {}) {
        this.logger = createLogger('BaseObject');
        
        this._schema = schema;
        this._options = options;
        
        // Get default values from schema
        const defaults = this._getDefaults(schema);
        const mergedData = { ...defaults, ...data };
        
        // Set core fields
        this.id = mergedData.id || this._generateId(options);
        this._isNew = mergedData._isNew || false;
        this.date_create = mergedData.date_create || new Date().toISOString();
        this.date_update = mergedData.date_update || new Date().toISOString();
        
        // Set all other fields from schema
        Object.keys(schema).forEach(key => {
            if (key !== 'id' && key !== 'date_create' && key !== 'date_update') {
                this[key] = mergedData[key];
            }
        });

        this.logger.debug(`Created object with ID: ${this.id}`);
    }

    /**
     * Extract default values from schema
     * @param {Object} schema - Schema definition
     * @returns {Object} Default values
     */
    _getDefaults(schema) {
        const defaults = {};
        
        Object.keys(schema).forEach(key => {
            const fieldSchema = schema[key];
            if (fieldSchema.hasOwnProperty('default')) {
                defaults[key] = typeof fieldSchema.default === 'function' 
                    ? fieldSchema.default() 
                    : fieldSchema.default;
            }
        });
        
        return defaults;
    }

    /**
     * Generate unique ID for the object
     * @param {Object} options - Options for ID generation
     * @returns {string} Generated ID
     */
    _generateId(options = {}) {
        if (options.generateId && typeof options.generateId === 'function') {
            const customId = options.generateId();
            this.logger.debug(`Generated custom ID: ${customId}`);
            return customId;
        }
        
        const id = typeof crypto !== 'undefined' && crypto.randomUUID 
            ? crypto.randomUUID()
            : Date.now().toString(36) + Math.random().toString(36).substr(2);
            
        this.logger.debug(`Generated default ID: ${id}`);
        return id;
    }

    /**
     * Get the schema definition for this object
     * @returns {Object} Schema definition
     */
    getSchema() {
        return this._schema;
    }

    /**
     * Get a plain object representation (for JSON serialization)
     * @param {Object} options - Serialization options
     * @returns {Object} Plain object
     */
    toJSON(options = {}) {
        const obj = {};
        const excludeFields = options.exclude || ['_schema', '_options', 'logger'];

        Object.keys(this).forEach(key => {
            // Include _isNew flag even though it starts with underscore
            if (!excludeFields.includes(key) && (!key.startsWith('_') || key === '_isNew')) {
                obj[key] = this[key];
            }
        });

        this.logger.debug(`Serialized object to JSON`, { id: this.id, fields: Object.keys(obj).length });
        return obj;
    }
}

export default BaseObject;